import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Imperative confirmation gate for prod-mutating actions.
 *
 * Mounted once at the root via <ProdConfirmationProvider>. Components
 * that need to gate a mutation call useConfirmProdAction() to obtain an
 * async function returning a Promise<boolean> — true means the operator
 * typed PROD and clicked Confirm; false means they cancelled / hit Esc.
 *
 * The api client (useApi) calls this automatically for mutating
 * requests when the active env is prod, so screens generally do not
 * need to wire it up themselves.
 */

export interface ConfirmProdOptions {
  /**
   * Short human-readable description of the action being confirmed,
   * e.g. "DELETE /api/v1/admin/recipes/abc". Shown to the operator.
   */
  action: string;
}

export type ConfirmProdAction = (
  options: ConfirmProdOptions,
) => Promise<boolean>;

interface PendingRequest {
  action: string;
  resolve: (confirmed: boolean) => void;
}

const ConfirmProdContext = createContext<ConfirmProdAction | null>(null);

const REQUIRED_TEXT = "PROD";

export function ProdConfirmationProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingRequest | null>(null);
  const [typed, setTyped] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  // Tracks the element that was focused before the modal opened so we
  // can restore focus on close — basic a11y hygiene.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const confirm = useCallback<ConfirmProdAction>((options) => {
    return new Promise<boolean>((resolve) => {
      setTyped("");
      setPending({ action: options.action, resolve });
    });
  }, []);

  const close = useCallback(
    (confirmed: boolean) => {
      setPending((current) => {
        if (current) current.resolve(confirmed);
        return null;
      });
      setTyped("");
    },
    [],
  );

  // Focus management: capture previously-focused element when opening,
  // focus the input, and restore focus when closing.
  useEffect(() => {
    if (!pending) return;
    previouslyFocusedRef.current =
      (document.activeElement as HTMLElement | null) ?? null;
    // Defer to next frame so the input is mounted.
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(id);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [pending]);

  // Esc-to-cancel + simple focus trap (Tab cycles between input ↔
  // cancel ↔ confirm).
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close(false);
        return;
      }
      if (e.key !== "Tab") return;
      const order: Array<HTMLElement | null> = [
        inputRef.current,
        cancelButtonRef.current,
        confirmButtonRef.current,
      ];
      const focusables = order.filter((el): el is HTMLElement => el !== null);
      if (focusables.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? focusables.indexOf(active) : -1;
      const dir = e.shiftKey ? -1 : 1;
      const nextIdx = (idx + dir + focusables.length) % focusables.length;
      e.preventDefault();
      focusables[nextIdx]?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, close]);

  const matches = typed === REQUIRED_TEXT;

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!matches) return;
      close(true);
    },
    [matches, close],
  );

  return (
    <ConfirmProdContext.Provider value={confirm}>
      {children}
      {pending ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="prod-confirm-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => {
            // Backdrop click cancels.
            if (e.target === e.currentTarget) close(false);
          }}
        >
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md rounded border border-red-500/60 bg-bg-card p-5 shadow-xl"
          >
            <h2
              id="prod-confirm-title"
              className="text-base font-semibold text-text-light"
            >
              Confirm production action
            </h2>
            <p className="mt-2 text-sm text-text-muted">
              You are about to perform a mutating request against{" "}
              <span className="font-semibold text-red-400">production</span>.
              Type <code className="rounded bg-bg-card-elevated px-1">PROD</code>{" "}
              to proceed.
            </p>
            <div className="mt-3 rounded bg-bg-card-elevated px-2 py-1 text-xs text-text-muted">
              <span className="text-text-light">{pending.action}</span>
            </div>
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              aria-label="Type PROD to confirm"
              placeholder="PROD"
              className="mt-4 w-full rounded border border-bg-card-elevated bg-bg-card px-2 py-1.5 text-sm text-text-light focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                ref={cancelButtonRef}
                type="button"
                onClick={() => close(false)}
                className="rounded bg-bg-card-elevated px-3 py-1.5 text-sm text-text-light hover:opacity-90"
              >
                Cancel
              </button>
              <button
                ref={confirmButtonRef}
                type="submit"
                disabled={!matches}
                className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </ConfirmProdContext.Provider>
  );
}

/**
 * Returns the imperative prod confirmation function. Throws if used
 * outside <ProdConfirmationProvider>.
 */
export function useConfirmProdAction(): ConfirmProdAction {
  const ctx = useContext(ConfirmProdContext);
  if (!ctx) {
    throw new Error(
      "useConfirmProdAction must be used inside <ProdConfirmationProvider>",
    );
  }
  return ctx;
}

/**
 * Useful for tests / consumers that just want to know whether the
 * provider is mounted (e.g. a fallback path that skips confirmation
 * when the provider isn't wired).
 */
export function useMaybeConfirmProdAction(): ConfirmProdAction | null {
  return useContext(ConfirmProdContext);
}

